/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { getCSCEndpoint } from '../../../config/Config';

import ConditionalTooltip from '../FieldComponents/ConditionalTooltip';
import { Option } from '../FieldComponents/Select';
import ContactListening from '../../ContactListening/ContactListening';
import ContactListeningModal from '../../ContactListening/ContactListeningModal';
import {
  ContactData,
  defaultContactData,
} from '../../../api/ContactListening/schema';
import DataState from '../../../Types/DataState';
import { QueryVariablesContactListeningData } from '../../../api/Naws';
import MoreInfoIcon from '../../../commons/MoreInfoIcon';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';

type Value = Option['value'];
type ValueWithLink = { text: Value; href: string };
type ValueWithConcession = {
  category: string;
  processName: string;
  commentText: string;
};
type DisplayValue =
  | Value
  | ValueWithLink
  | ValueWithConcession
  | (Value | ValueWithLink | ValueWithConcession)[];

type ActionLink = { text: string; action: () => void };

export type DisplayObject = {
  label: string;
  value: DisplayValue;
  callback?: ActionLink;
  contactStartDate?: string;
  contactEndDate?: string;
  contactHandlerId?: string;
};

interface DisplaySavedProps {
  mapKey?: string;
  displayValues: DisplayObject[] | DisplayObject[][];
  handlers?: {
    edit?: () => void;
    remove?: (arg0: number) => void;
  };
  disableHandlers?: Record<'edit' | 'delete', string | false>;
}

const FieldDisplay = ({
  label,
  value,
  callback,
  contactStartDate,
  contactEndDate,
  contactHandlerId,
}: DisplayObject): React.ReactElement => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const valueArray = Array.isArray(value) ? value : [value];
  const isEmptyLabelAndEmptyValue = label === '' && value === '';
  const isLabelEndWithColon = label.endsWith(':');
  const captilizedLabel =
    label === '' ? label : label.charAt(0).toUpperCase() + label.slice(1);

  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingContactId, setRecordingContactId] = useState('');
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();

  const CLClickstream = {
    slotID: 'open-contact-listening-modal',
    tag1: 'Team',
    tag2: 'Coaching History',
  };

  const concessionFeatureEnabled = isFeatureEnabled(
    Feature.ENABLE_CONCESSION_COMPLIANCE
  );

  const elementArray = valueArray.map(val =>
    typeof val === 'object' && 'href' in val ? (
      <ContactListening
        contactID={`${val.text}`}
        setContactListening={setContactListeningData}
        setContactListeningState={setContactListeningDataState}
        setContactListeningParams={setContactListeningParams}
        setRecordingContactId={setRecordingContactId}
        setShowRecordingModal={setShowRecordingModal}
        clickstreamTags={{
          'data-csa-c-slot-id': 'coaching-form-field-array-link-slot',
          'data-csa-c-content-id': `coaching-form-field-array-link`,
          'data-csa-c-link-url': `${val.href}`,
          'data-csa-c-tag2': 'Customer Comments',
        }}
        cscEndpoint={`${val.href.replace(getCSCEndpoint(), '')}`}
        cscTarget={'_blank'}
        contactStartDate={contactStartDate}
        contactEndDate={contactEndDate}
        contactHandlerId={contactHandlerId}
        CLClickstream={CLClickstream}
      />
    ) : concessionFeatureEnabled &&
      typeof val === 'object' &&
      'processName' in val ? (
      <MoreInfoIcon
        processName={val.processName}
        category={val.category}
        concessionFeedbackCommentText={val.commentText}
      />
    ) : (
      // @ts-ignore
      <>{val}</>
    )
  );

  const selectedText = intl.formatMessage(
    {
      id: 'cs_perfportal_count_selected',
      defaultMessage: `{0} selected`,
    },
    { 0: valueArray.length }
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleCallback = useCallback(callback?.action || (() => {}), []);

  return (
    <>
      <ContactListeningModal
        loadingState={contactListeningDataState}
        showModal={showRecordingModal}
        contactListeningParams={contactListeningParams}
        setShowModal={setShowRecordingModal}
        contactId={recordingContactId}
        contactListeningData={contactListeningData}
        CLClickstream={{
          ...CLClickstream,
          tag3: 'Contact Listening Modal',
        }}
      />
      {elementArray.length > 1 && (
        <Modal
          title={label}
          open={open}
          onClose={() => setOpen(false)}
          scrollContainer='viewport'
          width='515px'
          closeLabel={intl.formatMessage({
            id: 'cs_perfportal_popover_close',
            defaultMessage: 'close',
          })}
          aria-describedby={`${label}`}
        >
          <Text type='h200'>{selectedText}</Text>
          <Text tag='ul'>
            {elementArray.map((element, index) => (
              // These items are never edited, removed, or reordered
              <li key={`${label}_${index}_${JSON.stringify(element.props)}`}>
                {element}
              </li>
            ))}
          </Text>
        </Modal>
      )}
      <Text>
        <strong>
          {captilizedLabel}
          {isLabelEndWithColon || isEmptyLabelAndEmptyValue ? '' : ':'}
        </strong>{' '}
        {(elementArray.length > 1 && (
          <Link
            data-csa-c-type='element'
            data-csa-c-slot-id='coaching-form-field-display-array-link-slot'
            data-csa-c-content-id='coaching-form-display-field-array-open-link'
            type='secondary'
            onClick={() => setOpen(true)}
          >
            {selectedText}
          </Link>
        )) ||
          elementArray[0]}{' '}
        {callback && (
          <Link
            type='secondary'
            data-csa-c-type='element'
            data-csa-c-slot-id='coaching-form-field-display-array-link-slot'
            data-csa-c-content-id='coaching-form-display-field-array-callback-link'
            onClick={handleCallback}
          >
            {callback.text}
          </Link>
        )}
      </Text>{' '}
    </>
  );
};

const isNestedArray = (
  array: DisplayObject[] | DisplayObject[][]
): array is DisplayObject[][] => {
  return !!(array as DisplayObject[][])[0][0];
};

const FieldArrayDisplay: React.FC<DisplaySavedProps> = ({
  mapKey,
  displayValues,
  handlers,
  disableHandlers,
}: DisplaySavedProps): React.ReactElement => {
  const intl = useIntl();
  const caseManagementEnabled = isFeatureEnabled(Feature.CASE_MANAGEMENT);

  return (
    <div>
      <Row>
        <Column
          width={caseManagementEnabled ? '100%' : handlers ? '90%' : '100%'}
        >
          <Box type='outline' spacingInset='400'>
            {isNestedArray(displayValues)
              ? displayValues.map((array, index) => {
                  return (
                    <div key={`${mapKey}_${index}`}>
                      {index > 0 && (
                        <Row spacingInset='300 none'>
                          <Divider />
                        </Row>
                      )}
                      {Array.isArray(array) &&
                        array.map((value, keyIndex) => (
                          <FieldDisplay
                            key={`${mapKey}_${value.label}_${value.value}_${keyIndex}`}
                            label={value.label}
                            value={value.value}
                            callback={value.callback}
                            contactStartDate={value.contactStartDate}
                            contactEndDate={value.contactEndDate}
                            contactHandlerId={value.contactHandlerId}
                          />
                        ))}
                    </div>
                  );
                })
              : displayValues.map((value, index) => (
                  <FieldDisplay
                    key={`${mapKey}_${index}_${value.label}_${value.value}`}
                    label={value.label}
                    value={value.value}
                    callback={value.callback}
                    contactStartDate={value.contactStartDate}
                    contactEndDate={value.contactEndDate}
                    contactHandlerId={value.contactHandlerId}
                  />
                ))}
          </Box>
        </Column>
        {handlers ? (
          <Column>
            <Row spacing='300'>
              {handlers?.edit && (
                <ConditionalTooltip tooltip={disableHandlers?.edit}>
                  <Button
                    data-csa-c-type='button'
                    data-csa-c-slot-id='coaching-form-layout-button-slot'
                    data-csa-c-content-id='coaching-form-layout-edit-button'
                    type='icon'
                    onClick={handlers.edit}
                    disabled={!!disableHandlers?.edit}
                    aria-label={intl.formatMessage({
                      id: 'cs_perfportal_coaching_edit',
                      defaultMessage: 'Edit',
                    })}
                  >
                    <Icon tokens={editTokens} />
                  </Button>
                </ConditionalTooltip>
              )}

              {handlers?.remove && (
                <ConditionalTooltip tooltip={disableHandlers?.delete}>
                  <Button
                    data-csa-c-type='button'
                    data-csa-c-slot-id='coaching-form-layout-button-slot'
                    data-csa-c-content-id='coaching-form-layout-delete-button'
                    type='icon'
                    onClick={handlers.remove}
                    disabled={!!disableHandlers?.delete}
                    aria-label={intl.formatMessage({
                      id: 'cs_perfportal_coaching_delete',
                      defaultMessage: 'Delete',
                    })}
                  >
                    <Icon tokens={trashTokens} />
                  </Button>
                </ConditionalTooltip>
              )}
            </Row>
          </Column>
        ) : null}
      </Row>
    </div>
  );
};

export default FieldArrayDisplay;
